import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { WebsiteLayout } from '@/components/website/WebsiteLayout';
import { MapPin, Phone, ArrowRight, ChevronDown, ChevronUp, CheckCircle, Building2 } from 'lucide-react';
import api from '@/utils/api';

export default function SEOPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [properties, setProperties] = useState([]);
  const [settings, setSettings] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    api.get(`/public/seo-pages/${slug}`).then(r => setPage(r.data)).catch(() => {});
    api.get('/public/properties').then(r => setProperties(r.data || [])).catch(() => {});
    api.get('/public/website-settings').then(r => setSettings(r.data)).catch(() => {});
  }, [slug]);

  if (!page) return (
    <WebsiteLayout>
      <div className="min-h-[60vh] flex items-center justify-center text-slate-500">Loading...</div>
    </WebsiteLayout>
  );

  const relatedProperties = properties.filter(p => {
    const matchLoc = page.location && (p.location || '').toLowerCase().includes(page.location.toLowerCase());
    const matchType = page.property_type && (p.property_type || '').toLowerCase().includes(page.property_type.toLowerCase());
    return matchLoc || matchType;
  }).slice(0, 6);

  // JSON-LD Schema for SEO
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": page.meta_title || page.title,
    "description": page.meta_description,
    "publisher": {
      "@type": "Organization",
      "name": "Ganesa Homestate Developers",
      "url": settings?.website || "https://ganesahomestate.com"
    }
  };

  const faqSchema = page.faq && page.faq.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": page.faq.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  } : null;

  return (
    <WebsiteLayout>
      <Helmet>
        <title>{page.meta_title || page.title}</title>
        <meta name="description" content={page.meta_description} />
        <meta name="keywords" content={page.keywords} />
        <link rel="canonical" href={`https://ganesahomestate.com/location/${slug}`} />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      </Helmet>

      {/* Hero */}
      <section className="bg-[#0A0A0F] py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#DAA520]/5 to-transparent"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 text-[#DAA520] text-sm mb-4">
            <MapPin size={16} /> {page.location}
            {page.property_type && <span>/ {page.property_type}</span>}
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">{page.h1 || page.title}</h1>
          <p className="text-slate-400 text-lg max-w-3xl">{page.meta_description}</p>
          <div className="mt-8 flex gap-4">
            <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#DAA520] to-[#B8860B] text-[#0A0A0F] font-bold rounded-xl hover:shadow-xl hover:shadow-[#DAA520]/25 transition-all hover:scale-105">
              Enquire Now <ArrowRight size={18} />
            </Link>
            <a href={`tel:${settings?.phone}`} className="inline-flex items-center gap-2 px-8 py-4 border-2 border-[#DAA520]/40 text-[#DAA520] font-bold rounded-xl hover:bg-[#DAA520]/10 transition-all">
              <Phone size={18} /> Call Now
            </a>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2">
              <article className="prose prose-lg max-w-none prose-headings:text-[#0A0A0F] prose-a:text-[#DAA520] prose-li:text-slate-600"
                dangerouslySetInnerHTML={{ __html: page.content }} />
            </div>
            <div>
              <div className="bg-[#0A0A0F] rounded-2xl p-6 sticky top-24">
                <h3 className="text-white font-bold text-lg mb-4">Quick Enquiry</h3>
                <p className="text-slate-400 text-sm mb-4">Interested in {page.title}? Contact us now!</p>
                <Link to="/contact" className="block w-full text-center py-3.5 bg-[#DAA520] text-[#0A0A0F] font-bold rounded-xl hover:bg-[#B8860B] transition-all mb-3">
                  Get Details
                </Link>
                <a href={`tel:${settings?.phone}`} className="block w-full text-center py-3.5 border border-[#DAA520]/40 text-[#DAA520] font-bold rounded-xl hover:bg-[#DAA520]/10 transition-all">
                  {settings?.phone || 'Call Now'}
                </a>
                <div className="mt-6 pt-6 border-t border-[#1A1A25]">
                  <h4 className="text-white text-sm font-semibold mb-3">Why Ganesa Homestate?</h4>
                  {['RERA Approved', 'Clear Titles', 'Easy EMI', 'Premium Locations'].map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-slate-400 text-xs mb-2">
                      <CheckCircle size={14} className="text-[#DAA520]" /> {f}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Properties */}
      {relatedProperties.length > 0 && (
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl font-bold text-[#0A0A0F] mb-8">Available Properties in {page.location}</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedProperties.map(p => (
                <Link key={p.id} to={`/properties/${p.id}`} className="group bg-white rounded-2xl p-5 shadow-sm hover:shadow-xl transition-all border border-slate-100">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-xl bg-[#0A0A0F] flex items-center justify-center shrink-0">
                      <Building2 size={24} className="text-[#DAA520]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0A0A0F] group-hover:text-[#DAA520] transition-colors">{p.property_name}</h3>
                      <p className="text-sm text-slate-500 mt-1">{p.location}</p>
                      {p.price_range && <p className="text-sm font-bold text-[#DAA520] mt-2">{p.price_range}</p>}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {page.faq && page.faq.length > 0 && (
        <section className="py-16" data-testid="faq-section">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl font-bold text-[#0A0A0F] mb-8 text-center">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {page.faq.map((f, i) => (
                <div key={i} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors">
                    <span className="font-semibold text-[#0A0A0F] pr-4">{f.q}</span>
                    {openFaq === i ? <ChevronUp size={20} className="text-[#DAA520] shrink-0" /> : <ChevronDown size={20} className="text-slate-400 shrink-0" />}
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-5 text-slate-600 text-sm leading-relaxed border-t border-slate-100 pt-4">{f.a}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </WebsiteLayout>
  );
}
